import Logger from '../lib/logger'

const HIGH_NRC_THRESHOLD = 100000
const HIGH_MRC_THRESHOLD = 10000

/**
 * Shared pricing validation utilities.
 *
 * Used by price_calculator_controller and supplier_quote_form_controller
 * to validate NRC/MRC pricing fields.
 */

/**
 * Validate pricing inputs and return an array of warning messages.
 * @param {number} nrc - Non-Recurring Charge value
 * @param {number} mrc - Monthly Recurring Charge value
 * @returns {string[]} Array of warning messages (empty if valid)
 */
export function validatePricing(nrc, mrc) {
  const warnings = []

  if (nrc > HIGH_NRC_THRESHOLD) {
    warnings.push('NRC seems unusually high')
  }

  if (mrc > HIGH_MRC_THRESHOLD) {
    warnings.push('MRC seems unusually high')
  }

  if (nrc === 0 && mrc === 0) {
    warnings.push('Both NRC and MRC are zero')
  }

  return warnings
}

/**
 * Parse a pricing value safely, returning 0 for invalid input.
 * @param {*} value - The raw value to parse
 * @returns {number} The parsed number or 0
 */
export function parsePricingValue(value) {
  const num = parseFloat(value)
  return isNaN(num) ? 0 : num
}

/**
 * Format a number with exactly 2 decimal places.
 * @param {number} number - The number to format
 * @returns {string} Formatted number string
 */
export function formatPricing(number) {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(number)
}

/**
 * Calculate Year 1 Total from NRC and MRC.
 * @param {number} nrc - Non-Recurring Charge
 * @param {number} mrc - Monthly Recurring Charge
 * @returns {number} Year 1 Total = NRC + (MRC * 12)
 */
export function calculateYear1Total(nrc, mrc) {
  return nrc + (mrc * 12)
}

/**
 * Get currency symbol for a given currency code.
 * @param {string} currency - ISO 4217 currency code (e.g., 'USD')
 * @returns {string} The currency symbol
 */
export function getCurrencySymbol(currency) {
  const symbols = {
    'USD': '$',
    'EUR': '€',
    'GBP': '£',
    'BRL': 'R$',
    'JPY': '¥',
    'CAD': 'C$',
    'AUD': 'A$'
  }
  return symbols[currency] || currency + ' '
}

export default {
  validatePricing,
  parsePricingValue,
  formatPricing,
  calculateYear1Total,
  getCurrencySymbol
}
